/**
 * Aggregates API results for the command palette (global search, issues, review, partners, contacts).
 */
import { useMemo } from "react";
import { useDebounce } from "use-debounce";
import { useQuery } from "@tanstack/react-query";
import { useGlobalSearch } from "@/hooks/api/use-folders";
import { useIssuesList } from "@/hooks/api/use-issues-list";
import { useReviewItems } from "@/hooks/api/use-review-center";
import { useMe } from "@/hooks/api/use-auth";
import { getPartners } from "@/data/api/receivers";
import { getContacts } from "@/data/api/address-book";
import { apiGet } from "@/lib/api-client";
import { COMPANIES_QUERY_KEYS } from "@/hooks/api/use-companies";
import type { UserType } from "@/types/account";
import type { CompanyResponse } from "@/types/api/admin";
import type { GlobalSearchResponse } from "@/types/api/folders";
import { parseShortcut, getCurrentUserType } from "./command-menu-search";
import type { EntityType } from "./command-menu-context";

export type CommandPaletteSection =
    | "folders"
    | "containers"
    | "documents"
    | "issues"
    | "review"
    | "businessPartners"
    | "contacts"
    | "accounts";

export interface CommandPaletteItem {
    id: string;
    section: CommandPaletteSection;
    title: string;
    subtitle?: string;
    url: string;
}

export interface CommandPaletteGroup {
    key: CommandPaletteSection;
    items: CommandPaletteItem[];
}

function basePathForUser(userType: UserType | null): string {
    if (userType === "SHIPPER") return "/shipper";
    if (userType === "RECEIVER") return "/receiver";
    if (userType === "ADMIN") return "/admin";
    if (userType === "FREIGHT_FORWARDER") return "/freight-forwarder";
    return "";
}

function includeGlobalFolders(effectiveEntity: EntityType | null, userType: UserType | null): boolean {
    return userType === "SHIPPER" && !effectiveEntity;
}

function includeGlobalContainers(effectiveEntity: EntityType | null): boolean {
    if (!effectiveEntity) return true;
    return effectiveEntity === "container";
}

function includeGlobalDocuments(effectiveEntity: EntityType | null, userType: UserType | null): boolean {
    if (userType !== "SHIPPER") return false;
    if (!effectiveEntity) return true;
    return effectiveEntity === "document";
}

function documentResultUrl(
    base: string,
    documentId: string,
    containerId: string | null | undefined,
    userType: UserType | null,
): string {
    if (userType === "SHIPPER") {
        return `${base}/documents/${documentId}`;
    }
    if (userType === "RECEIVER" && containerId) {
        return `${base}/containers/${containerId}`;
    }
    return `${base}/containers`;
}

function reviewTypeLabel(t: string): string {
    return t.replace(/_/g, " ");
}

export function useCommandPaletteSearch(
    query: string,
    isOpen: boolean,
    selectedEntity: EntityType | null,
): {
    groups: CommandPaletteGroup[];
    flatSelectableItems: CommandPaletteItem[];
    isLoading: boolean;
    effectiveEntity: EntityType | null;
    apiQuery: string;
} {
    const [debouncedQuery] = useDebounce(query, 280);
    const userType = getCurrentUserType();
    const basePath = basePathForUser(userType);
    const { data: me } = useMe();

    const parsed = parseShortcut(debouncedQuery);
    const effectiveEntity = parsed.entity || selectedEntity;
    const apiQuery = parsed.entity ? parsed.searchTerm.trim() : debouncedQuery.trim();

    const enabled = isOpen && apiQuery.length >= 1;

    const globalSearchEnabled =
        enabled &&
        userType !== "ADMIN" &&
        userType !== null &&
        effectiveEntity !== "receiver" &&
        effectiveEntity !== "address" &&
        effectiveEntity !== "shipper";

    const { data: globalData, isLoading: loadingGlobal } = useGlobalSearch(apiQuery, {
        enabled: globalSearchEnabled,
    });

    const shipperAuxiliary = enabled && userType === "SHIPPER" && !effectiveEntity;

    const { data: issuesData, isLoading: loadingIssues } = useIssuesList(
        {
            search: apiQuery,
            limit: 10,
            skip: 0,
            show_resolved: false,
        },
        shipperAuxiliary,
    );

    const reviewQueryEnabled = shipperAuxiliary;

    const { data: reviewData, isLoading: loadingReview } = useReviewItems(
        {
            status: "pending",
            search: apiQuery,
            limit: 10,
            skip: 0,
        },
        reviewQueryEnabled,
    );

    const partnersQueryEnabled =
        enabled && userType === "SHIPPER" && !!me?.company_id && (!effectiveEntity || effectiveEntity === "receiver");

    const partnersQuery = useQuery({
        queryKey: ["command-palette", "partners", me?.company_id],
        queryFn: () => getPartners(me!.company_id!),
        enabled: partnersQueryEnabled,
        staleTime: 60_000,
    });

    const contactsQueryEnabled =
        enabled &&
        (userType === "SHIPPER" || userType === "RECEIVER") &&
        (!effectiveEntity || effectiveEntity === "address");

    const contactsQuery = useQuery({
        queryKey: ["command-palette", "contacts", apiQuery],
        queryFn: () => getContacts({ search: apiQuery }),
        enabled: contactsQueryEnabled,
    });

    const { data: companies = [], isLoading: loadingCompanies } = useQuery({
        queryKey: COMPANIES_QUERY_KEYS.list({ is_active: true, limit: 500 }),
        queryFn: async (): Promise<CompanyResponse[]> => {
            return await apiGet<CompanyResponse[]>("/companies", {
                params: { is_active: true, limit: 500 },
            });
        },
        enabled: enabled && userType === "ADMIN",
        staleTime: 60_000,
    });

    const groups = useMemo((): CommandPaletteGroup[] => {
        const out: CommandPaletteGroup[] = [];
        const push = (key: CommandPaletteSection, items: CommandPaletteItem[]) => {
            if (items.length === 0) return;
            out.push({ key, items });
        };

        if (userType === "ADMIN") {
            if (effectiveEntity && effectiveEntity !== "account") {
                return [];
            }
            const q = apiQuery.toLowerCase();
            const filtered = companies
                .filter(
                    (c) =>
                        (c.company_name?.toLowerCase().includes(q) ?? false) ||
                        (c.legal_name?.toLowerCase().includes(q) ?? false) ||
                        (c.primary_email?.toLowerCase().includes(q) ?? false),
                )
                .slice(0, 25);
            push(
                "accounts",
                filtered.map((c) => ({
                    id: `account-${c.company_id}`,
                    section: "accounts" as const,
                    title: c.company_name || c.legal_name || "Company",
                    subtitle: c.primary_email || c.company_type,
                    url: `${basePath}/companies/${c.company_id}/edit`,
                })),
            );
            return out;
        }

        const gs: GlobalSearchResponse | undefined = globalData;

        if (gs && includeGlobalFolders(effectiveEntity, userType)) {
            push(
                "folders",
                gs.folders.map((f) => ({
                    id: `folder-${f.id}`,
                    section: "folders" as const,
                    title: f.name,
                    subtitle: f.folder_type === "container" ? f.container_number || "Container folder" : "Folder",
                    url: `${basePath}/documents?tab=myDrive&folder=${f.id}`,
                })),
            );
        }

        if (gs && includeGlobalContainers(effectiveEntity)) {
            push(
                "containers",
                gs.containers.map((c) => ({
                    id: `container-${c.id}`,
                    section: "containers" as const,
                    title: c.name || c.container_number || "Container",
                    subtitle: c.container_number && c.container_number !== c.name ? c.container_number : undefined,
                    url: `${basePath}/containers/${c.container_id ?? c.id}`,
                })),
            );
        }

        if (gs && includeGlobalDocuments(effectiveEntity, userType)) {
            push(
                "documents",
                gs.documents.map((d) => ({
                    id: `document-${d.id}`,
                    section: "documents" as const,
                    title: d.name,
                    subtitle: d.document_type || undefined,
                    url: documentResultUrl(basePath, d.id, d.container_id, userType),
                })),
            );
        }

        if (issuesData?.issues?.length && shipperAuxiliary) {
            push(
                "issues",
                issuesData.issues.map((issue) => ({
                    id: `issue-${issue.issue_id}`,
                    section: "issues" as const,
                    title: issue.title,
                    subtitle: issue.container_number || issue.severity,
                    url: `${basePath}/issues?search=${encodeURIComponent(apiQuery)}`,
                })),
            );
        }

        if (reviewData?.items?.length && reviewQueryEnabled) {
            push(
                "review",
                reviewData.items.map((item) => ({
                    id: `review-${item.review_item_id}`,
                    section: "review" as const,
                    title: reviewTypeLabel(item.review_type),
                    subtitle: item.container_id ? String(item.container_id).slice(0, 8) : undefined,
                    url: `${basePath}/review-center?item=${item.review_item_id}`,
                })),
            );
        }

        if (partnersQuery.data?.length && partnersQueryEnabled) {
            const q = apiQuery.toLowerCase();
            const filtered = partnersQuery.data
                .filter(
                    (p) =>
                        (p.company_name?.toLowerCase().includes(q) ?? false) ||
                        (p.legal_name?.toLowerCase().includes(q) ?? false) ||
                        (p.primary_email?.toLowerCase().includes(q) ?? false) ||
                        (p.country?.toLowerCase().includes(q) ?? false),
                )
                .slice(0, 15);
            push(
                "businessPartners",
                filtered.map((p) => ({
                    id: `partner-${p.company_id}`,
                    section: "businessPartners" as const,
                    title: p.company_name || p.legal_name || "Partner",
                    subtitle: p.company_type.replace(/_/g, " "),
                    url: `${basePath}/business-partners/${p.company_id}`,
                })),
            );
        }

        const contactRows = contactsQuery.data ?? [];
        if (contactRows.length && contactsQueryEnabled) {
            push(
                "contacts",
                contactRows.slice(0, 15).map((entry) => ({
                    id: `contact-${entry.id}`,
                    section: "contacts" as const,
                    title: `${entry.firstName} ${entry.lastName}`.trim() || entry.email || "Contact",
                    subtitle: entry.email || entry.role,
                    url: `${basePath}/address-book?search=${encodeURIComponent(apiQuery)}`,
                })),
            );
        }

        return out;
    }, [
        userType,
        basePath,
        globalData,
        effectiveEntity,
        issuesData,
        reviewData,
        partnersQuery.data,
        partnersQueryEnabled,
        contactsQuery.data,
        contactsQueryEnabled,
        companies,
        apiQuery,
        shipperAuxiliary,
        reviewQueryEnabled,
    ]);

    const flatSelectableItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

    const isLoading =
        (globalSearchEnabled && loadingGlobal) ||
        (shipperAuxiliary && loadingIssues) ||
        (reviewQueryEnabled && loadingReview) ||
        (partnersQueryEnabled && partnersQuery.isLoading) ||
        (contactsQueryEnabled && contactsQuery.isLoading) ||
        (enabled && userType === "ADMIN" && loadingCompanies);

    return {
        groups,
        flatSelectableItems,
        isLoading,
        effectiveEntity,
        apiQuery,
    };
}
